import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
} from "@angular/core";

import {
    CONTENTS_SIZES_CONSTANTS,
    EDITORIAL_MEDIA_QUERY_CONSTANTS,
} from "@hermes/aphrodite/video";
import { Context } from "@hermes/app-core";
import {
    PRODUCT_IN_GRID_DESKTOP,
    PRODUCT_IN_GRID_MOBILE,
} from "@hermes/fragments/product-utils";
import { formatAssetUrl } from "@hermes/utils/helpers";
import { SCENE7_IMAGE_BACKGROUND_COLOR } from "@hermes/utils-generic/constants";
export const SNACKABLE_VIDEO = "snackable_video";
export const PRODUCT_IN_GRID = "product_in_grid";
export const HERO_PRODUCT = "hero_product";
export const CROSS_SELL_PRODUCT = "cross_sell_product";

export type MediaType =
    | typeof SNACKABLE_VIDEO
    | typeof PRODUCT_IN_GRID
    | typeof HERO_PRODUCT
    | typeof CROSS_SELL_PRODUCT;

@Component({
    selector: "h-image-resizer",
    templateUrl: "./image-resizer.component.html",
    styleUrls: ["./image-resizer.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImageResizerComponent implements OnChanges {
    @Input()
    public desktopUrl!: string;
    @Input()
    public desktopAlt: string = "";
    @Input()
    public mediaType!: MediaType;
    @Input()
    public pictureCssClass!: string;
    @Input()
    public mobileUrl!: string;
    @Input()
    public mobileAlt?: string;
    @Input()
    public pictureId?: string;
    @Input()
    public videoName?: string;
    @Input()
    public videoId?: string;
    @Input()
    public imageId?: string;
    @Input()
    public isMobile: boolean = false;
    @Input()
    public productIndex: number = 0;

    @Output()
    public imageInViewPort = new EventEmitter<boolean>();
    @Output()
    public mediaVisibleInViewport = new EventEmitter<string>();

    public contentsSizes = CONTENTS_SIZES_CONSTANTS;
    public editiorialMediaQueries = EDITORIAL_MEDIA_QUERY_CONSTANTS;
    public productGridImageUrl = "";
    public snackableVideo: MediaType = SNACKABLE_VIDEO;
    public productInGrid: MediaType = PRODUCT_IN_GRID;
    public heroProduct: MediaType = HERO_PRODUCT;
    public crossSellProduct: MediaType = CROSS_SELL_PRODUCT;

    constructor(private cd: ChangeDetectorRef, private context: Context) {}

    public ngOnChanges(changes: SimpleChanges): void {
        if (this.desktopUrl && changes.desktopUrl) {
            this.desktopUrl = this.cleanUpURL(this.desktopUrl);
            this.constructProductGridImageUrl();
        }
        if (this.mobileUrl && changes.mobileUrl) {
            this.mobileUrl = this.cleanUpURL(this.mobileUrl);
        }
        if (
            changes?.mediaType?.currentValue !==
            changes?.mediaType?.previousValue
        ) {
            this.constructProductGridImageUrl();
        }
    }

    /**
     * Removes space from a string.
     */
    public cleanUpURL(input: string): string {
        return formatAssetUrl(input);
    }

    /**
     * Allow to construct snackable desktop image url
     */
    public constructSnackableImageUrl(
        imageWidth: number,
        size: string,
    ): string {
        if (!this.desktopUrl) {
            return "";
        }
        if (this.isMobileSize(size) && this.mobileUrl) {
            return this.gridResultItemImageUrl(
                imageWidth,
                this.mobileUrl,
                1,
                0,
            );
        }

        return this.gridResultItemImageUrl(imageWidth, this.desktopUrl, 1, 0);
    }

    /**
     * True if in mobile breakpoints
     *
     * @param size : size label
     */
    public isMobileSize(size: string): boolean {
        return ["xsmall", "small"].includes(size);
    }

    /**
     * Allow to construct product grid image url
     */
    public constructProductGridImageUrl(): void {
        if (!this.desktopUrl) {
            return;
        }
        this.productGridImageUrl = this.gridResultItemImageUrl(
            this.getSizeImage(),
            this.desktopUrl,
        );

        this.cd.detectChanges();
    }

    public getSizeImage(): number {
        if (this.mediaType === CROSS_SELL_PRODUCT) {
            return PRODUCT_IN_GRID_MOBILE;
        }
        return PRODUCT_IN_GRID_DESKTOP;
    }

    /**
     * Allow to map entry element to key/value obj
     */
    public mapObject(
        object: Record<keyof typeof EDITORIAL_MEDIA_QUERY_CONSTANTS, string>,
    ): Array<{
        key: keyof typeof EDITORIAL_MEDIA_QUERY_CONSTANTS;
        value: string;
    }> {
        return Object.keys(object).map((key) => ({
            key: key as keyof typeof EDITORIAL_MEDIA_QUERY_CONSTANTS,
            value: object[key as keyof typeof EDITORIAL_MEDIA_QUERY_CONSTANTS],
        }));
    }

    public handleImageInViewPort(isInViewport: boolean): void {
        this.imageInViewPort.emit(isInViewport);
        if (isInViewport) {
            this.mediaVisibleInViewport.emit(`img-${this.imageId}`);
        }
    }

    public isInServerMode(): boolean {
        return this.context.isInServerMode();
    }

    /**
     * Format size to get the proper url image resized.
     *
     * @param url the size
     * @param width the size
     * @return the proper url image resized
     */
    public gridResultItemImageUrl(
        width: number,
        url: string,
        multiplier?: number,
        quality?: number,
    ): string {
        if (multiplier || quality) {
            const qualityParameter = quality ? `&qlt=${quality}` : "";
            return `${url}?fit=wrap,0&wid=${
                width * (multiplier ?? 1)
            }${qualityParameter}`;
        }
        return `${url}&${SCENE7_IMAGE_BACKGROUND_COLOR}&wid=${width}&hei=${width}`;
    }
}
